import React, { useState, useEffect } from "react";
import { Button, ButtonSet, Form, TextInput } from "carbon-components-react";
import { Formik } from "formik";

import { AppShape } from "@eave/shared/types/api";
import { thereWasAProblem } from "@eave/shared/utils/alerts";

import { initialValuesFromLoanApp, validationSchemaFromLoanApp } from "./form-values";
import { AppDelta, updateBorrowerDependents, updateLoanApp } from "./shared";
import { borrowersAscending } from "@eave/shared/utils/borrowers";

const styles = require("../form.module.scss");

interface Props {
  app: AppShape;
  reloadApp: () => void;
}

const DependentsAgesEditor = ({ app, reloadApp }: Props) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { id, borrowers } = app;

  const updateBorrowers = async (values: AppDelta) => {
    const updates = borrowersAscending(borrowers).map(borrower =>
      updateBorrowerDependents(
        id,
        borrower.borrower_entity,
        borrowers[borrower.borrower_entity],
        values[`borrower${borrower.borrower_entity}_dependents_list_of_ages`],
      ),
    );

    return Promise.all(updates);
  };

  const onSubmit = async (values: AppDelta) => {
    setSaving(true);
    try {
      await updateLoanApp(id, values);
      await updateBorrowers(values);
      reloadApp();
    } catch (e) {
      thereWasAProblem("saving dependents changes");
      throw e;
    }
  };

  useEffect(() => {
    if (saving) {
      setSaving(false);
    }
  }, [saving]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>Number of dependents and their ages</div>
      <div className={styles.details}>
        <p>
          Use commas to separate dependents&apos; ages. For example, for 3 dependents aged 8, 12,
          and 79, enter <code>8, 12, 79</code>.
        </p>
        <p>
          Make sure that any dependents are not shared by multiple borrowers, even if they&apos;re
          part of the same household.
        </p>
        <p>Leave the input blank if a borrower has no dependents.</p>
      </div>
      <Formik
        initialValues={initialValuesFromLoanApp(app)}
        validationSchema={validationSchemaFromLoanApp(app)}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ dirty, errors, handleBlur, handleChange, handleSubmit, resetForm, values }) => {
          const hasErrors = Object.keys(errors).length > 0;

          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
              className={styles.form}
            >
              <div className={styles.buttons}>
                {!editing ? (
                  <Button kind="secondary" size="sm" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                ) : (
                  <ButtonSet>
                    <Button
                      kind="tertiary"
                      size="sm"
                      onClick={() => {
                        setEditing(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button disabled={!dirty || hasErrors} kind="primary" size="sm" type="submit">
                      Save
                    </Button>
                  </ButtonSet>
                )}
              </div>
              <div className={styles.inputs}>
                {borrowersAscending(borrowers).map((borrower, index) => (
                  <TextInput
                    id={`borrower${borrower.borrower_entity}-dependents-list-of-ages`}
                    key={`borrower${borrower.borrower_entity}-dependents-list-of-ages`}
                    name={`borrower${borrower.borrower_entity}_dependents_list_of_ages`}
                    value={
                      values[`borrower${borrower.borrower_entity}_dependents_list_of_ages`] || ""
                    }
                    labelText={`Borrower ${index + 1} ages of dependents`}
                    invalid={
                      !!errors[`borrower${borrower.borrower_entity}_dependents_list_of_ages`]
                    }
                    invalidText={
                      errors[`borrower${borrower.borrower_entity}_dependents_list_of_ages`]
                    }
                    disabled={!editing || saving}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                ))}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DependentsAgesEditor;
